import { supabase } from "./supabaseClient";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_ANON_KEY;

function getMetaEndpointUrl() {
  if (!SUPABASE_URL) return "";
  return `${SUPABASE_URL.replace(/\/$/, "")}/functions/v1/metaConversions`;
}

function isConversionTrackingAllowed() {
  return canUseAnalytics() || canUseMarketing();
}

import { canUseAnalytics, canUseMarketing } from "@/components/cookies/consentManager";

/**
 * Read a cookie value by name.
 */
function getCookie(name) {
  if (typeof document === "undefined") return "";
  const match = document.cookie.match(new RegExp("(^|;\\s*)" + name + "=([^;]*)"));
  return match ? decodeURIComponent(match[2]) : "";
}

/**
 * Build fbc value from fbclid if possible (Meta recommendation).
 * Format: fb.1.<timestamp>.<fbclid>
 */
function buildFbcFromFbclid() {
  if (typeof window === "undefined") return "";
  const url = new URL(window.location.href);
  const fbclid = url.searchParams.get("fbclid");
  if (!fbclid) return "";
  const ts = Math.floor(Date.now() / 1000);
  return `fb.1.${ts}.${fbclid}`;
}

function getFbpFbc() {
  // Meta cookie names are typically _fbp / _fbc
  const fbp = getCookie("_fbp");
  let fbc = getCookie("_fbc");
  if (!fbc) fbc = buildFbcFromFbclid();
  return { fbp, fbc };
}

function generateEventId() {
  // Prefer a stable UUID for Pixel/CAPI deduplication
  try {
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
  } catch (_) {}
  // Fallback: timestamp + random
  return `evt_${Date.now()}_${Math.random().toString(16).slice(2)}`;
}

function trackPixelEvent(eventName, params, eventId) {
  if (typeof window === "undefined") return;
  if (!canUseMarketing()) return;
  if (!window.fbq) return;

  try {
    // Meta pixel supports eventID for dedup between Pixel + CAPI
    window.fbq("track", eventName, params || {}, { eventID: eventId });
  } catch (_) {
    // swallow
  }
}

export async function sendMetaEvent({
  eventName,
  email,
  externalId,
  customData,
  eventSourceUrl,
  actionSource = "website",
  eventId,
  allowCustom = false,
  testEventCode,
  requireMarketing = false,
} = {}) {
  // RGPD/CNIL (cadre légal) — Option 2 "Performance" :
// - Pixel/retargeting : uniquement si consentement MARKETING.
// - CAPI conversions : peut partir même si cookies refusés, à condition de rester sobre
//   (finalité = mesure de performance, minimisation, pas d'audiences/retargeting sans opt-in).
// - Pour les events top-of-funnel, tu peux choisir de les conditionner au MARKETING en appelant requireMarketing=true.
  if (requireMarketing && !canUseMarketing()) return null;

  const resolvedEventId = eventId || generateEventId();
  // Pixel uniquement si consentement marketing (retargeting / cookies)
  if (canUseMarketing()) {
    // Send browser event (Pixel) + server event (CAPI) with the SAME event_id for dedup.
    trackPixelEvent(eventName, customData || {}, resolvedEventId);
  }

  try {
    const hasMarketing = canUseMarketing();
    const { fbp, fbc } = hasMarketing ? getFbpFbc() : { fbp: "", fbc: "" };

    const body = {
      event_name: eventName,
      event_time: Math.floor(Date.now() / 1000),
      event_id: resolvedEventId,
      action_source: actionSource,
      event_source_url: eventSourceUrl || (typeof window !== "undefined" ? window.location.href : ""),
      allow_custom: allowCustom ? 1 : 0,
      test_event_code: testEventCode,
      user_data: {
        email,
        external_id: externalId,
        fbp,
        fbc,
      },
      custom_data: customData || {},
    };

    // Never throw: tracking must not break UX
    await supabase.functions.invoke("metaConversions", { body });
  } catch (_e) {
    // swallow
  }

  return resolvedEventId;
}
